/**
 * DistributionMonitor - Dashboard premium de monitoramento da distribuição automática
 * 
 * Design premium com:
 * - Header com ícone decorativo e badge de tempo real
 * - Filtro por tipo de conversa (individual/grupo)
 * - Filtro por tipo de entidade (conversation/deal)
 * - Cards de métricas usando DashboardStatCard
 * - Componentes com gradientes, shimmer e hover effects
 * - Layout responsivo otimizado
 * 
 * Exibe em tempo real:
 * - Métricas de distribuição (cards)
 * - Status dos agentes
 * - Departamentos com distribuição ativa
 * - Distribuições recentes
 * - Gráfico de volume por hora
 * 
 * @version 2.0.0
 * @date 2026-01-20
 */

import { RefreshCw, Activity, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDistributionMonitor } from '@/hooks/useDistributionMonitor';
import { DistributionStatsCards } from '@/components/distribution/DistributionStatsCards';
import { DistributionTypeFilter } from '@/components/distribution/DistributionTypeFilter';
import { DistributionEntityFilter } from '@/components/distribution/DistributionEntityFilter';
import { AgentStatusGrid } from '@/components/distribution/AgentStatusGrid';
import { DepartmentDistributionList } from '@/components/distribution/DepartmentDistributionList';
import { RecentDistributionsTable } from '@/components/distribution/RecentDistributionsTable';
import { DistributionVolumeChart } from '@/components/distribution/DistributionVolumeChart';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

export default function DistributionMonitor() {
  const {
    stats,
    agentStatuses,
    departments,
    recentDistributions,
    filteredDistributions,
    hourlyVolume,
    isLoading,
    typeFilter,
    setTypeFilter,
    entityFilter,
    setEntityFilter,
    refresh,
  } = useDistributionMonitor();

  // Filtra distribuições por tipo de entidade além do tipo de conversa
  const entityFilteredDistributions = filteredDistributions.filter(d => {
    if (entityFilter === 'all') return true;
    return d.entityType === entityFilter;
  });

  // Contadores para o filtro de entidade
  const entityCounts = {
    all: filteredDistributions.length,
    conversation: filteredDistributions.filter(d => d.entityType === 'conversation' || !d.entityType).length,
    deal: filteredDistributions.filter(d => d.entityType === 'deal').length,
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header Premium - Responsivo */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Ícone decorativo - menor em mobile */}
          <div className="flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 shadow-lg shadow-primary/10">
            <Activity className="h-5 w-5 sm:h-7 sm:w-7 text-primary" />
          </div>
          
          <div>
            <div className="flex items-center flex-wrap gap-1.5 sm:gap-3">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground tracking-tight">
                Monitoramento de Distribuição
              </h1>
              
              {/* Badge de tempo real com pulse */}
              <Badge 
                variant="outline" 
                className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1 sm:gap-1.5 shrink-0 text-xs"
              >
                <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-emerald-500"></span>
                </span>
                Ao vivo
              </Badge>
            </div>
            <p className="text-muted-foreground mt-0.5 sm:mt-1 text-sm sm:text-base line-clamp-1 sm:line-clamp-none">
              Acompanhe em tempo real a distribuição automática de conversas e negócios
            </p>
          </div>
        </div>
        
        {/* Ações */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            asChild
            className="gap-2"
          >
            <Link to="/tenant/distribution/queues">
              <Layers className="h-4 w-4" />
              Filas
            </Link>
          </Button>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refresh} 
            disabled={isLoading}
            className={cn(
              'gap-2 transition-all duration-300',
              'hover:bg-primary/10 hover:text-primary hover:border-primary/30',
              'active:scale-95'
            )}
          >
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros - dois grupos lado a lado */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Filtro por tipo de entidade (conversation/deal) */}
        <DistributionEntityFilter
          value={entityFilter}
          onChange={setEntityFilter}
          counts={entityCounts}
          isLoading={isLoading}
        />

        {/* Filtro por tipo de conversa (individual/grupo) - só aparece quando filtrando conversas */}
        {entityFilter !== 'deal' && (
          <DistributionTypeFilter
            value={typeFilter}
            onChange={setTypeFilter}
            counts={{
              all: recentDistributions.length,
              individual: recentDistributions.filter(d => d.conversationType === 'individual').length,
              group: recentDistributions.filter(d => d.conversationType === 'group').length,
            }}
            isLoading={isLoading}
          />
        )}
      </div>

      {/* Cards de métricas */}
      <DistributionStatsCards stats={stats} isLoading={isLoading} />

      {/* Grid principal - 2 colunas em telas grandes */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-6">
        {/* Coluna esquerda */}
        <div className="space-y-6">
          <AgentStatusGrid agents={agentStatuses} isLoading={isLoading} />
          <DepartmentDistributionList departments={departments} isLoading={isLoading} />
        </div>

        {/* Coluna direita - usa distribuições filtradas por entidade */}
        <div className="space-y-6">
          <RecentDistributionsTable distributions={entityFilteredDistributions} isLoading={isLoading} />
        </div>
      </div>

      {/* Gráfico de volume - largura total */}
      <DistributionVolumeChart data={hourlyVolume} isLoading={isLoading} />
    </div>
  );
}
